// Catálogo de definiciones de métricas por sección.
// Alimenta los tooltips "?" y el modal "¿Qué significan estas métricas?".

export interface MetricDefinition {
	key: string;
	title: string;
	description: string; // resumen corto (tooltip)
	details: string;     // explicación extendida (modal glosario)
}

export const METRICS_GLOSSARY: Record<string, MetricDefinition[]> = {
	users: [
		{
			key: 'ghost_users',
			title: 'Usuarios Fantasma',
			description: 'Usuarios registrados sin actividad real ni datos cargados.',
			details: 'Cuentas que se registraron pero nunca cargaron información relevante (patrimonio, ingresos, gastos) ni usaron el chat. Sirve para medir cuántos registros son inactivos desde el inicio. Un porcentaje alto puede indicar fricción en el onboarding.'
		},
		{
			key: 'subusers',
			title: 'Subusuarios',
			description: 'Cuentas secundarias dentro de un usuario principal. La app permite crear perfiles familiares/dependientes.',
			details: 'Un usuario principal puede crear sub-perfiles dentro de su cuenta (nombre + apellido, email opcional). Esto NO es un programa de referidos: el subuser depende del parent. La tabla "Top Cuentas con Subusuarios" lista los usuarios principales con más sub-perfiles asociados. Click en una fila para ver los subusers de ese parent específico.'
		},
	],

	patrimony: [
		{
			key: 'patrimony_adoption',
			title: 'Adopción Patrimonio',
			description: 'Proporción de clientes que cargaron al menos un activo de patrimonio.',
			details: 'Mide cuántos clientes activaron el módulo de patrimonio cargando al menos un activo (físico, digital o liquidez) sobre el total de clientes. Es el indicador principal de adopción de la feature de patrimonio.'
		},
		{
			key: 'patrimony_by_plan',
			title: 'Patrimonio por Plan',
			description: 'Patrimonio total, promedio y cantidad de usuarios, comparado entre Free y Premium.',
			details: 'Compara el patrimonio agregado entre usuarios Free y Premium: suma total cargada, promedio por usuario y cantidad de usuarios con datos en cada plan. Útil para entender si los usuarios Premium gestionan más patrimonio.'
		},
	],

	plans: [
		{
			key: 'conversion_rates',
			title: 'Tasas de Conversión',
			description: 'Clientes que pasaron a Premium dentro de 30, 60 y 90 días desde su registro.',
			details: 'Para cada ventana (30/60/90 días) muestra cuántos clientes registrados convirtieron a Premium y el porcentaje sobre el total de clientes. Permite ver qué tan rápido convierten los usuarios tras registrarse.'
		},
		{
			key: 'time_to_conversion',
			title: 'Tiempo a Conversión',
			description: 'Días promedio (mín/máx) entre registro y compra del primer plan.',
			details: 'Promedio, mínimo y máximo de días que tarda un cliente desde que se registra hasta que compra su primer plan Premium, junto con el total de conversiones consideradas. Indica la velocidad del ciclo de venta.'
		},
		{
			key: 'funnel',
			title: 'Funnel de Conversión',
			description: 'Embudo Registrados → Activos 30d → Premium activos con tasas de activación y conversión.',
			details: 'Embudo de tres etapas: usuarios registrados, los que estuvieron activos en los últimos 30 días, y los que tienen Premium activo. Incluye la tasa de activación (activos/registrados) y la tasa de conversión (premium/activos).'
		},
		{
			key: 'renewal_rate',
			title: 'Tasa de Renovación',
			description: 'Porcentaje de usuarios con plan vencido que volvieron a renovar.',
			details: 'De los usuarios cuyo plan venció, cuántos renovaron y el porcentaje resultante. Mide la retención de pago una vez que el plan caduca.'
		},
		{
			key: 'winback_candidates',
			title: 'Candidatos a Win-back',
			description: 'Usuarios con plan vencido sin renovar en los últimos 90 días.',
			details: 'Lista de usuarios cuyo plan venció y aún no renovaron, con la fecha de vencimiento y los días transcurridos. Es la base para campañas de recuperación (win-back).'
		},
		{
			key: 'revenue',
			title: 'Revenue',
			description: 'Ingresos históricos totales, transacciones y desglose mensual.',
			details: 'Ingreso acumulado histórico, cantidad de transacciones pagadas y la serie mensual de revenue, transacciones y ticket promedio. Es la métrica financiera principal de la sección.'
		},
		{
			key: 'paid_vs_complimentary',
			title: 'Pago vs Cortesía',
			description: 'Planes activos pagados reales vs planes de cortesía otorgados.',
			details: 'Separa los planes Premium activos entre los que fueron efectivamente pagados y los otorgados como cortesía, mostrando el porcentaje de pago real. Evita sobreestimar ingresos por cuentas de cortesía.'
		},
	],

	chat: [
		{
			key: 'chat_adoption',
			title: 'Adopción Chat IA',
			description: 'Proporción de clientes que usaron el chat IA al menos una vez.',
			details: 'Cuántos clientes usaron alguna vez el chat de IA sobre el total de clientes. Indicador principal de adopción de la feature de chat.'
		},
		{
			key: 'post_chat_conversion',
			title: 'Conversión Post-Chat',
			description: 'Usuarios Free que compraron Premium después de usar el chat.',
			details: 'De los usuarios Free que usaron el chat, cuántos compraron Premium después y el porcentaje resultante. Mide el impacto del chat IA como driver de conversión.'
		},
		{
			key: 'chat_by_plan',
			title: 'Uso de Chat por Plan',
			description: 'Cantidad de chatters, mensajes y promedio por usuario, comparado Free vs Premium.',
			details: 'Compara el uso del chat entre Free y Premium: cuántos usuarios usaron el chat (chatters), total de mensajes y promedio de mensajes por usuario en cada plan.'
		},
	],

	activity: [
		{
			key: 'dau_wau_mau',
			title: 'DAU / WAU / MAU',
			description: 'Usuarios activos diarios, semanales y mensuales.',
			details: 'DAU = usuarios activos en el día, WAU = en la semana, MAU = en el mes. Son las métricas estándar de actividad recurrente de la plataforma.'
		},
		{
			key: 'stickiness',
			title: 'Stickiness',
			description: 'Relación DAU/MAU: qué tan seguido vuelven los usuarios.',
			details: 'Stickiness = DAU / MAU expresado en porcentaje. Indica con qué frecuencia los usuarios mensuales vuelven a diario. El nivel (Bajo/Medio/Alto) categoriza ese porcentaje.'
		},
		{
			key: 'feature_adoption',
			title: 'Adopción por Feature',
			description: 'Cantidad y porcentaje de usuarios que usan cada módulo de la app.',
			details: 'Para cada módulo (Patrimonio, Ingresos, Gastos, Deudas, Metas, Chat IA) muestra cuántos usuarios lo usan y el porcentaje sobre el total de clientes. Identifica qué features tienen más tracción.'
		},
	],
};

export default METRICS_GLOSSARY;
